import { useState, useRef } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { compressImage } from "../lib/image";
import { Camera, X, Images } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PhotoUpload({ photos, setPhotos }) {
  const [uploading, setUploading] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newOnes = [];
    for (const original of files) {
      let file = original;
      try {
        // Shrink phone photos client-side so they always upload
        file = await compressImage(original, { maxDim: 1920, quality: 0.85 });
      } catch (err) {
        console.warn(`Image compression failed for ${original.name}; uploading original:`, err);
        // If compression fails (e.g. HEIC the browser can't decode), upload the original
        file = original;
      }
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await axios.post(`${API}/photos/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60_000,
        });
        newOnes.push({ id: data.id, url: `${API}/photos/${data.id}` });
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Upload failed";
        toast.error(`Couldn't upload ${original.name}: ${detail}`);
      }
    }
    setPhotos([...photos, ...newOnes]);
    setUploading(false);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const remove = (id) => setPhotos(photos.filter((p) => p.id !== id));

  return (
    <div className="space-y-3" data-testid="photo-upload">
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-xl overflow-hidden border border-[#E5E0D8] bg-[#F1ECDC]">
              <img src={p.url} alt="garden" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute top-1.5 right-1.5 bg-black/65 text-white rounded-full p-1 opacity-90 hover:opacity-100"
                data-testid={`remove-photo-${p.id}`}
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => cameraRef.current && cameraRef.current.click()}
          className="h-24 rounded-xl border-2 border-dashed border-[#C8C0B0] hover:border-[#4F6F52] bg-white text-[#2C3E35] hover:text-[#4F6F52] flex flex-col items-center justify-center gap-1.5 transition-colors"
          data-testid="take-photo-button"
          disabled={uploading}
        >
          <Camera size={26} weight="duotone" className="text-[#4F6F52]" />
          <span className="text-sm font-semibold">Take photo</span>
        </button>
        <button
          type="button"
          onClick={() => galleryRef.current && galleryRef.current.click()}
          className="h-24 rounded-xl border-2 border-dashed border-[#C8C0B0] hover:border-[#4F6F52] bg-white text-[#2C3E35] hover:text-[#4F6F52] flex flex-col items-center justify-center gap-1.5 transition-colors"
          data-testid="gallery-photo-button"
          disabled={uploading}
        >
          <Images size={26} weight="duotone" className="text-[#7A5A12]" />
          <span className="text-sm font-semibold">From gallery</span>
        </button>
      </div>

      {/* Camera input — single shot, opens phone camera directly */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files))}
        data-testid="photo-camera-input"
      />
      {/* Gallery input — multi-select from device */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(Array.from(e.target.files))}
        data-testid="photo-gallery-input"
      />

      {uploading && <p className="text-xs text-[#4F6F52] font-medium">Uploading…</p>}
      <p className="text-xs text-[#73777B]">
        Tap <span className="font-semibold text-[#2C3E35]">Take photo</span> to open your camera, or pick existing photos from your gallery. You can add as many as you like.
      </p>
    </div>
  );
}
